import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { roomApi, bookingApi, organisationApi } from '../services/api';
import { buildAuthHeaders } from '../services/authStorage';
import { parseRRuleToHumanReadable } from '../utils/recurrenceParser';
import { useDialogDraft } from '../hooks/useDialogDraft';
import { buildDraftScopeKey, getDraftForScope, clearDraftForScope } from '../utils/dialogDraftStore';
import { formatDateForLocalInput } from '../utils/localDateTimeInput';
import Layout from './layout/Layout';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import './BookRoom.css';

// M007-F010 / #59: Group recurring instances into a single entry, with a
// truthful recurrence label instead of a hardcoded "Repeats weekly".
//
// A Google-imported MASTER row has google_recurring_event_id NULL (it IS
// the master — see google-calendar.service.ts's INSERT/UPDATE around lines
// 2421/2531) and carries its own google_recurrence_rule, so it still gets a
// seriesKey via the booking_type fallback below and is grouped as a
// (single-row) series. Reuse the same RRULE parser Dashboard.js uses —
// never a second parser — so the label reflects the rule itself: "Weekly
// on <Day>, until <Month Day, Year>" when the rule has an UNTIL, an
// honest open-ended "Weekly on <Day>" when it doesn't, or a generic
// "Recurring series" fallback only when there's genuinely no rule to parse
// (e.g. a native/legacy recurring booking with no google_recurrence_rule).
//
// When showAllInstances is true, series are shown one row per instance
// instead of grouped, so this also computes each instance row's own parsed
// label (_isRecurringInstance / _recurrenceLabel) - see the #59 audit
// follow-up comment below for the regression this closes.
//
// Exported (module scope, pure function of its arguments) so unit tests can
// exercise it directly without rendering the component.
export const groupBookRoomFormBookings = (bookings, showAllInstances) => {
  if (showAllInstances) {
    // #59 audit follow-up: showing all instances means each recurring
    // instance row is rendered individually rather than rolled into a
    // series card below, so it never gets _isRecurringSeries. Without a
    // label here, the per-instance "🔁 Recurring: <label>" indicator (and
    // its "part of a series" note / Term Planner link) had nothing to
    // render, silently dropping them for the whole toggled view. Compute
    // the same parsed label per row - same parser, no series-count/grouping
    // logic duplicated - and flag it separately (_isRecurringInstance) so
    // the grouped-view branch below is untouched.
    return bookings.map((booking) => {
      if (!booking.google_recurring_event_id) return booking;
      return {
        ...booking,
        _isRecurringInstance: true,
        _recurrenceLabel: booking.google_recurrence_rule
          ? parseRRuleToHumanReadable(booking.google_recurrence_rule)
          : 'Series booking',
      };
    });
  }

  const grouped = [];
  const seenSeriesKeys = new Set();

  // Build a deterministic key for a series. Prefer google_recurring_event_id; fall back to
  // booking_type + room + title so we still collapse instances even if the recurring id is
  // missing from the payload.
  const getSeriesKey = (b) => {
    if (b.google_recurring_event_id) return `gcal:${b.google_recurring_event_id}`;
    if (b.booking_type === 'recurring_instance' || b.booking_type === 'recurring') {
      return `fallback:${b.room_id || 'room'}:${b.title || 'untitled'}`;
    }
    return null;
  };

  for (const booking of bookings) {
    const seriesKey = getSeriesKey(booking);

    if (seriesKey) {
      if (seenSeriesKeys.has(seriesKey)) {
        continue; // Skip additional instances of the same series
      }
      seenSeriesKeys.add(seriesKey);

      // Count how many instances share this key
      const seriesInstances = bookings.filter((b) => getSeriesKey(b) === seriesKey);

      const recurrenceLabel = booking.google_recurrence_rule
        ? parseRRuleToHumanReadable(booking.google_recurrence_rule)
        : 'Recurring series';

      grouped.push({
        ...booking,
        _isRecurringSeries: true,
        _recurrenceCount: seriesInstances.length,
        _recurrenceLabel: recurrenceLabel,
      });
    } else {
      grouped.push(booking);
    }
  }

  return grouped;
};

const BookRoomForm = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    roomId: '',
    title: '',
    description: '',
    natureOfUse: 'dance_class',
    otherNatureDescription: '',
    bookingDate: '',
    startTime: '',
    endTime: '',
    bookingType: 'one-off',
    occurrences: 10
  });
  
  const [rooms, setRooms] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [organisation, setOrganisation] = useState(null);
  
  // My Bookings state
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [showAllInstances, setShowAllInstances] = useState(false); // M007-F010: Toggle to show all recurring instances
  
  // M004-F0069: Active Booking Climate Widget state
  const [activeBooking, setActiveBooking] = useState(null);
  const [climateData, setClimateData] = useState(null);
  const [climateLoading, setClimateLoading] = useState(false);
  
  // Edit/Delete state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // M004-F0613 / T-M004-0020: Edit Booking is edit-only (no "new" variant here).
  const editBookingDraft = useDialogDraft('book-room-edit-booking', editingBooking?.id, { active: showEditModal });

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const editModalDismissHandlers = useDismissOnOutsideClick(() => setShowEditModal(false));

  useEffect(() => {
    fetchRooms();
    fetchOrganisationSettings();
    fetchMyBookings();
  }, []);

  // M004-F0069: Check for active booking and fetch climate data
  const checkActiveBooking = useCallback((bookings) => {
    const now = new Date();
    const MINUTES_BEFORE = 20;
    const MINUTES_AFTER = 10;
    
    const active = bookings.find(booking => {
      if (booking.status !== 'approved') return false;
      const start = new Date(booking.start_time);
      const end = new Date(booking.end_time);
      
      // Calculate the climate control window
      const windowStart = new Date(start.getTime() - MINUTES_BEFORE * 60 * 1000);
      const windowEnd = new Date(end.getTime() + MINUTES_AFTER * 60 * 1000);
      
      return now >= windowStart && now <= windowEnd;
    });
    
    setActiveBooking(active || null);
    return active;
  }, []);

  // M004-F0069: Fetch climate data for active booking
  const fetchClimateData = useCallback(async (roomId) => {
    if (!roomId) {
      setClimateData(null);
      return;
    }
    
    setClimateLoading(true);
    try {
      const response = await roomApi.getClimate(roomId);
      setClimateData(response.data?.data || response.data);
    } catch (err) {
      console.error('Failed to fetch climate data:', err);
      setClimateData(null);
    } finally {
      setClimateLoading(false);
    }
  }, []);

  // M004-F0069: Poll climate data when active booking exists
  useEffect(() => {
    if (!activeBooking) {
      setClimateData(null);
      return;
    }

    fetchClimateData(activeBooking.room_id);
    const interval = setInterval(() => fetchClimateData(activeBooking.room_id), 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [activeBooking, fetchClimateData]);

  // M004-F0069: Handle temperature change
  const handleTemperatureChange = async (newTemp) => {
    if (!activeBooking) return;
    
    try {
      await roomApi.setTemperature(activeBooking.room_id, newTemp);
      await fetchClimateData(activeBooking.room_id);
    } catch (err) {
      console.error('Failed to update temperature:', err);
      setMessage({ type: 'error', text: 'Failed to update temperature' });
    }
  };

  // M004-F0069: Handle occupancy change
  const handleOccupancyChange = async (newState) => {
    if (!activeBooking) return;
    
    try {
      await roomApi.setOccupancy(activeBooking.room_id, newState);
      await fetchClimateData(activeBooking.room_id);
    } catch (err) {
      console.error('Failed to update occupancy:', err);
      setMessage({ type: 'error', text: 'Failed to update occupancy' });
    }
  };

  // M007-F010 / #59: Group recurring instances into a single entry, with a
  // truthful, parsed recurrence label. See groupBookRoomFormBookings above
  // for the full rationale.
  const groupRecurringBookings = useCallback(
    (bookings) => groupBookRoomFormBookings(bookings, showAllInstances),
    [showAllInstances],
  );

  const fetchMyBookings = async () => {
    setLoadingBookings(true);
    try {
      const response = await fetch('/api/bookings/my', {
        headers: buildAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const bookings = data.data || [];
        // DEBUG: Log booking_type for recurring detection
        console.log('[BookRoomForm] Bookings fetched:', bookings.map(b => ({
          id: b.id,
          title: b.title,
          booking_type: b.booking_type,
          google_recurring_event_id: b.google_recurring_event_id
        })));
        setMyBookings(bookings);
        // M004-F0069: Check for active booking
        checkActiveBooking(bookings);
      }
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Edit booking handlers
  const openEditModal = (booking) => {
    // M004-F0622 / T-M004-0032: build the datetime-local value from LOCAL
    // date components (see src/utils/localDateTimeInput.js) so it round-trips
    // losslessly through `new Date(editForm.start_time)` on save below —
    // the previous `toISOString().slice(0, 16)` showed UTC digits mislabelled
    // as local, shifting every edit's stored time by the caller's UTC offset.
    setEditingBooking(booking);
    const draft = getDraftForScope(buildDraftScopeKey('book-room-edit-booking', booking.id));
    setEditForm({
      title: booking.title,
      description: booking.description || '',
      start_time: formatDateForLocalInput(booking.start_time),
      end_time: formatDateForLocalInput(booking.end_time),
      ...draft,
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);

    try {
      const response = await bookingApi.updateBooking(editingBooking.id, {
        title: editForm.title,
        description: editForm.description,
        start_time: new Date(editForm.start_time).toISOString(),
        end_time: new Date(editForm.end_time).toISOString()
      });

      setShowEditModal(false);
      clearDraftForScope(buildDraftScopeKey('book-room-edit-booking', editingBooking.id));
      setEditingBooking(null);
      fetchMyBookings();
      
      if (response.data.data.status === 'pending') {
        setMessage({ type: 'success', text: 'Booking updated and sent for approval.' });
      } else {
        setMessage({ type: 'success', text: 'Booking updated successfully.' });
      }
    } catch (err) {
      console.error('Failed to update booking:', err);
      setEditError(err.response?.data?.message || 'Failed to update booking. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  // M004-F0074-D/E: Handle delete booking with lifecycle support
  const handleDeleteBooking = async (bookingId) => {
    const booking = myBookings.find(b => b.id === bookingId);
    const isApproved = booking?.status === 'approved';
    
    const confirmMessage = isApproved 
      ? 'This approved booking will be submitted for deletion approval. Continue?'
      : 'Are you sure you want to delete this booking? This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setDeletingId(bookingId);
    try {
      const response = await bookingApi.deleteBooking(bookingId);
      fetchMyBookings();
      
      // M004-F0074-E: Handle delete_pending response
      if (response.data?.action === 'delete_pending') {
        setMessage({ type: 'success', text: 'Deletion request submitted for admin approval.' });
      } else {
        setMessage({ type: 'success', text: 'Booking deleted successfully.' });
      }
    } catch (err) {
      console.error('Failed to delete booking:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete booking. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setDeletingId(null);
    }
  };

  // M004-F0074-F: Undo delete request
  const handleUndoDelete = async (bookingId) => {
    if (!window.confirm('Cancel your deletion request?')) {
      return;
    }
    
    setDeletingId(bookingId);
    try {
      await bookingApi.undoDeleteRequest(bookingId);
      fetchMyBookings();
      setMessage({ type: 'success', text: 'Deletion request cancelled.' });
    } catch (err) {
      console.error('Failed to undo delete:', err);
      const errorMessage = err.response?.data?.message || 'Failed to cancel deletion request.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setDeletingId(null);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await roomApi.getRooms();
      // Get rooms data from response
      const roomsData = response.data?.data || response.data || [];
      
      // Add onboarding status tags to room names
      const roomsWithTags = roomsData.map(room => {
        if (room.onboarding_status && room.onboarding_status !== 'complete') {
          return {
            ...room,
            displayName: `${room.name} ⚠️ (Onboarding ${room.onboarding_status})`
          };
        }
        return { ...room, displayName: room.name };
      });
      
      setRooms(roomsWithTags);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
      
      // Check if user has no organisation
      if (err.response?.status === 403 || err.response?.data?.code === 'NO_ORGANISATION') {
        setMessage({ 
          type: 'error', 
          text: 'You are not linked to an organisation. Please complete the approval process to book rooms.' 
        });
      } else {
        setMessage({ type: 'error', text: 'Failed to load rooms. Please try again later.' });
      }
    }
  };

  const fetchOrganisationSettings = async () => {
    try {
      console.debug('fetchOrganisationSettings -> organisationApi.getMine');
      const response = await organisationApi.getMine();
      if (response.data?.data) {
        setOrganisation(response.data.data);
      } else if (response.data) {
        setOrganisation(response.data);
      }
    } catch (err) {
      // Non-critical - organisation settings are optional
      console.log('Organisation settings not available:', err.message);
    }
  };

  // Track the last synced room+date to avoid redundant syncs
  const [lastSynced, setLastSynced] = useState({ roomId: null, date: null });
  const [syncing, setSyncing] = useState(false);

  // Sync Google Calendar when room + date change (NOT on time changes)
  const syncCalendar = useCallback(async (roomId, bookingDate) => {
    if (!roomId || !bookingDate) return;
    
    // Skip if already synced for this room+date
    if (lastSynced.roomId === roomId && lastSynced.date === bookingDate) {
      console.log('Already synced for this room+date, skipping...');
      return;
    }

    setSyncing(true);
    try {
      const response = await fetch(`/api/rooms/${roomId}/sync`, {
        method: 'POST',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ date: bookingDate })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Google Calendar sync result:', data.data);
        setLastSynced({ roomId, date: bookingDate });
      }
    } catch (err) {
      console.error('Google Calendar sync failed:', err);
    } finally {
      setSyncing(false);
    }
  }, [lastSynced.roomId, lastSynced.date]);

  // Trigger sync ONLY when roomId or bookingDate changes
  useEffect(() => {
    const { roomId, bookingDate } = formData;
    if (roomId && bookingDate) {
      syncCalendar(roomId, bookingDate);
    }
  }, [formData.roomId, formData.bookingDate, syncCalendar]);

  // Auto-check availability when room and date/time are filled
  const checkAvailability = useCallback(async (roomId, bookingDate, startTime, endTime, bookingType, occurrences) => {
    if (!roomId || !bookingDate || !startTime || !endTime) {
      return;
    }

    setChecking(true);
    setAvailability(null);
    
    try {
      // Combine date with time
      const startDateTime = `${bookingDate}T${startTime}:00`;
      const endDateTime = `${bookingDate}T${endTime}:00`;
      
      const response = await fetch(`/api/rooms/${roomId}/availability`, {
        method: 'POST',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          startTime: startDateTime,
          endTime: endDateTime,
          bookingType: bookingType,
          occurrences: bookingType === 'weekly' ? occurrences : 1
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailability(data.data || data);
        
        if (data.data?.available || data.available) {
          setMessage({ type: 'success', text: '✓ Studio is FREE for this time slot!' });
        } else {
          const conflicts = data.data?.conflicts || data.conflicts || [];
          setMessage({ 
            type: 'warning', 
            text: `Studio is not available. ${conflicts.length} conflict(s) found.` 
          });
        }
      } else {
        // API endpoint may not exist yet - simulate available
        setAvailability({ available: true, simulated: true });
        setMessage({ type: 'info', text: '✓ Availability check pending - assuming studio is FREE' });
      }
    } catch (err) {
      // API endpoint may not exist yet - show pending message
      setAvailability({ available: true, simulated: true });
      setMessage({ type: 'info', text: '✓ Availability check pending - assuming studio is FREE' });
    } finally {
      setChecking(false);
    }
  }, []);

  // Trigger availability check when relevant fields change
  useEffect(() => {
    const { roomId, bookingDate, startTime, endTime, bookingType, occurrences } = formData;
    
    // Debounce the check
    const timeoutId = setTimeout(() => {
      if (roomId && bookingDate && startTime && endTime) {
        checkAvailability(roomId, bookingDate, startTime, endTime, bookingType, occurrences);
      } else {
        setAvailability(null);
        setMessage({ type: '', text: '' });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.roomId, formData.bookingDate, formData.startTime, formData.endTime, formData.bookingType, formData.occurrences, checkAvailability]);

  // M004-F0076: Handle form changes - auto-set endTime when startTime changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startTime') {
      // Auto-set endTime to 1 hour after startTime (default duration)
      const [startHour, startMin] = value.split(':').map(Number);
      const endTotalMinutes = (startHour * 60 + startMin) + 60; // Add 1 hour
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMin = endTotalMinutes % 60;
      
      // Only set if within valid range (max 11pm)
      let newEndTime = '';
      if (endHour <= 23) {
        newEndTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: value, endTime: newEndTime }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate time
    if (formData.startTime >= formData.endTime) {
      setMessage({ type: 'error', text: 'End time must be after start time' });
      return;
    }

    setSubmitting(true);
    
    try {
      const selectedRoom = rooms.find(r => r.id === parseInt(formData.roomId));
      const requiresApprovalDueToOnboarding = selectedRoom?.onboarding_status !== 'complete';
      
      // Combine date with time for API
      const startDateTime = `${formData.bookingDate}T${formData.startTime}:00`;
      const endDateTime = `${formData.bookingDate}T${formData.endTime}:00`;
      
      const response = await fetch(`/api/rooms/${formData.roomId}/bookings`, {
        method: 'POST',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          natureOfUse: formData.natureOfUse,
          otherNatureDescription: formData.otherNatureDescription,
          startTime: startDateTime,
          endTime: endDateTime,
          bookingType: formData.bookingType,
          occurrences: formData.bookingType === 'weekly' ? formData.occurrences : 1
        })
      });
      
      if (response.ok) {
        const requiresApproval = organisation?.booking_requires_approval || requiresApprovalDueToOnboarding;
        const approvalReason = requiresApprovalDueToOnboarding 
          ? 'You need to complete the onboarding process for this organisation.' 
          : 'Your organisation requires admin approval for bookings.';
        
        setMessage({ 
          type: 'success', 
          text: requiresApproval 
            ? `Booking request submitted! Awaiting admin approval. ${approvalReason}` 
            : 'Booking confirmed! It will sync to Google Calendar shortly.' 
        });
        
        // Reset form
        setFormData({
          roomId: '',
          title: '',
          description: '',
          natureOfUse: 'dance_class',
          otherNatureDescription: '',
          bookingDate: '',
          startTime: '',
          endTime: '',
          bookingType: 'one-off',
          occurrences: 10
        });
        setAvailability(null);
        
        // Refresh bookings list
        fetchMyBookings();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to create booking' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An error occurred while creating the booking' });
    } finally {
      setSubmitting(false);
    }
  };

  // Generate time options from 6:00 AM to 10:00 PM in 15 min increments
  // M004-F0075: Time select with 15-minute intervals
  // M004-F0076: Start time 6am-10pm, end time max 11pm
  const generateTimeOptions = () => {
    const options = [];
    // Start at 6am (6), end at 10pm (22) to allow at least 1hr booking before 11pm cutoff
    for (let hour = 6; hour <= 22; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const displayStr = `${hour > 12 ? hour - 12 : hour}:${min.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
        options.push({ value: timeStr, label: displayStr });
      }
    }
    return options;
  };

  // M004-F0076: Generate end time options - must be after start time, in 1-hour increments, max 11pm
  const generateEndTimeOptions = (startTime) => {
    if (!startTime) return [];
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const startTotalMinutes = startHour * 60 + startMin;
    const maxEndHour = 23; // 11pm max
    const options = [];
    
    // Generate options in 1-hour increments from start time
    for (let hours = 1; hours <= 6; hours++) {
      const endTotalMinutes = startTotalMinutes + (hours * 60);
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMin = endTotalMinutes % 60;
      
      // Don't go past 11pm (23:00)
      if (endHour > maxEndHour) break;
      
      const timeStr = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      const displayStr = `${endHour > 12 ? endHour - 12 : endHour}:${endMin.toString().padStart(2, '0')} ${endHour >= 12 ? 'PM' : 'AM'} (${hours}hr)`;
      options.push({ value: timeStr, label: displayStr });
    }
    
    return options;
  };

  const timeOptions = generateTimeOptions();
  const endTimeOptions = generateEndTimeOptions(formData.startTime);

  // Helper function to format date/time
  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'status-badge status-approved';
      case 'pending': return 'status-badge status-pending';
      case 'rejected': return 'status-badge status-rejected';
      case 'cancelled': return 'status-badge status-cancelled';
      case 'delete_pending': return 'status-badge status-delete-pending';
      default: return 'status-badge';
    }
  };

  // Helper function to get status display text
  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return '✓ Approved';
      case 'pending': return '⏳ Waiting for Approval';
      case 'rejected': return '✗ Rejected';
      case 'cancelled': return '🚫 Cancelled';
      case 'delete_pending': return '⏱️ Delete Pending';
      default: return status;
    }
  };

  // M004-F0074: Check if booking is locked (within 20 minutes of start)
  const isBookingLocked = (booking) => {
    if (!booking) return false;
    const now = new Date();
    const bookingStart = new Date(booking.start_time);
    const minutesUntilStart = (bookingStart.getTime() - now.getTime()) / (1000 * 60);
    return minutesUntilStart < 20 && minutesUntilStart > -Infinity;
  };

  return (
    <Layout>
      <div className="book-room-container">
        <header className="page-header">
          <h1>Book a Room</h1>
          <p className="subtitle">Submit your room booking request</p>
        </header>

        {/* My Bookings Section */}
        <section className="my-bookings-section">
          <div className="section-header">
            <h2>My Bookings</h2>
            {/* M007-F010: Toggle to show/hide individual recurring instances */}
            {myBookings.some(b => b.google_recurring_event_id) && (
              <label className="toggle-instances">
                <input
                  type="checkbox"
                  checked={showAllInstances}
                  onChange={(e) => setShowAllInstances(e.target.checked)}
                />
                <span>Show all instances</span>
              </label>
            )}
          </div>
          {loadingBookings ? (
            <div className="bookings-loading">
              <div className="spinner"></div>
              <span>Loading your bookings...</span>
            </div>
          ) : myBookings.length === 0 ? (
            <div className="no-bookings">
              <p>You haven't made any bookings yet.</p>
            </div>
          ) : (
            <div className="bookings-list">
              {groupRecurringBookings(myBookings).map(booking => {
                // DEBUG: Log each booking's recurring fields when rendering
                console.log('[BookRoomForm] Rendering card:', booking.id, 'booking_type:', booking.booking_type, 'recurring_id:', booking.google_recurring_event_id);
                return (
                <div key={booking.id} className={`booking-card booking-${booking.status}`}>
                  <div className="booking-header">
                    <h3 className="booking-title">{booking.title}</h3>
                    <span className={getStatusBadgeClass(booking.status)}>
                      {getStatusText(booking.status)}
                    </span>
                  </div>
                  {/* M007-F010: Recurring series indicator - show "Repeats weekly (X instances)" */}
                  {booking._isRecurringSeries && (
                    <div className="recurring-info recurring-series">
                      <span className="recurring-badge">
                        🔁 {booking._recurrenceLabel} ({booking._recurrenceCount} instances)
                      </span>
                      {(booking.term_slot_booking_id || booking.imported_from_google || booking.imported_recurring_booking_id || booking.imported_recurring_booking || booking.term_management_required) && (
                        <a className="term-management-link" href={booking.term_management_url || '/app/teacher/term-planner'} data-testid="WEB-TERM-014">
                          Managed in Term Planner
                        </a>
                      )}
                    </div>
                  )}
                  {/* #59 audit follow-up: this MR originally deleted the old
                      "single instance indicator (when showing all)" branch
                      here as dead code — correctly, in the default grouped
                      view, where booking._isRecurringSeries is always true
                      for anything with a seriesKey so `!booking._isRecurringSeries`
                      never ran. But it IS reachable in the showAllInstances
                      toggled view: groupBookRoomFormBookings returns each
                      instance row there with _isRecurringInstance (not
                      _isRecurringSeries) plus a pre-parsed _recurrenceLabel
                      (same parseRRuleToHumanReadable parser, computed once
                      in the grouping function so it isn't duplicated here).
                      Restored, keyed off that flag. */}
                  {booking._isRecurringInstance && (
                    <div className="recurring-info">
                      <span className="recurring-badge">
                        🔁 Recurring: {booking._recurrenceLabel}
                      </span>
                      <p className="recurring-notice">
                        <em>ℹ️ Part of a recurring series</em>
                      </p>
                      {(booking.term_slot_booking_id || booking.imported_from_google || booking.imported_recurring_booking_id || booking.imported_recurring_booking || booking.term_management_required) && (
                        <a className="term-management-link" href={booking.term_management_url || '/app/teacher/term-planner'} data-testid="WEB-TERM-014">
                          Managed in Term Planner
                        </a>
                      )}
                    </div>
                  )}
                  <div className="booking-details">
                    <div className="booking-detail">
                      <span className="detail-label">📍 Room:</span>
                      <span className="detail-value">{booking.room_name}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">📅 Start:</span>
                      <span className="detail-value">{formatDateTime(booking.start_time)}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">🕐 End:</span>
                      <span className="detail-value">{formatDateTime(booking.end_time)}</span>
                    </div>
                  </div>
                  {booking.rejection_reason && (
                    <div className="booking-rejection">
                      <strong>Reason for rejection:</strong> {booking.rejection_reason}
                    </div>
                  )}
                  {/* M004-F0074: Show locked status if within 20 minutes */}
                  {booking.status === 'approved' && isBookingLocked(booking) && (
                    <div className="booking-locked-notice">
                      🔒 This booking is locked (starts in less than 20 minutes)
                    </div>
                  )}
                  <div className="booking-actions">
                    {/* M004-F0074-F: Undo Delete button for delete_pending status */}
                    {booking.status === 'delete_pending' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => handleUndoDelete(booking.id)}
                        disabled={deletingId === booking.id}
                      >
                        {deletingId === booking.id ? 'Processing...' : '↩️ Undo Delete'}
                      </button>
                    ) : (
                      <>
                        {/* M004-F0062: Edit button - enabled for Super Admin, Admin, Teacher; disabled for event_simple */}
                        {booking.status !== 'cancelled' && booking.status !== 'rejected' && 
                         user?.role !== 'event_simple' && !isBookingLocked(booking) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => openEditModal(booking)}
                          >
                            ✏️ Edit
                          </button>
                        )}
                        {/* M004-F0074: Delete button - hidden if locked */}
                        {!isBookingLocked(booking) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteBooking(booking.id)}
                            disabled={deletingId === booking.id}
                          >
                            {deletingId === booking.id ? 'Deleting...' : '🗑️ Delete'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </section>

        {/* M004-F0069: Active Booking Climate Widget */}
        {activeBooking && user?.role !== 'student' && (
          <section className="climate-control-section">
            <h2>🌡️ Climate Control - Active Booking</h2>
            <div className="climate-widget">
              <div className="climate-widget-header">
                <h3>{activeBooking.room_name || `Room ${activeBooking.room_id}`}</h3>
                <span className="status-badge status-approved">Currently Active</span>
              </div>
              
              {climateLoading ? (
                <div className="climate-loading">
                  <div className="spinner"></div>
                  <span>Loading climate data...</span>
                </div>
              ) : climateData ? (
                <div className="climate-controls">
                  <div className="temperature-control">
                    <label>Temperature: {climateData.targetTemp || climateData.target_temp}°C</label>
                    <input
                      type="range"
                      min="15"
                      max="25"
                      step="0.5"
                      value={climateData.targetTemp || climateData.target_temp || 20}
                      onChange={(e) => handleTemperatureChange(parseFloat(e.target.value))}
                      className="temperature-slider"
                    />
                    <div className="temp-range">
                      <span>15°C</span>
                      <span>25°C</span>
                    </div>
                  </div>

                  <div className="occupancy-control">
                    <label>Room Occupancy:</label>
                    <div className="toggle-buttons">
                      <button
                        type="button"
                        className={`btn btn-sm ${(climateData.occupancyState || climateData.occupancy_state) === 'in_use' ? 'btn-success' : 'btn-outline'}`}
                        onClick={() => handleOccupancyChange('in_use')}
                      >
                        ● In Use
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${(climateData.occupancyState || climateData.occupancy_state) === 'unused' ? 'btn-danger-outline' : 'btn-outline'}`}
                        onClick={() => handleOccupancyChange('unused')}
                      >
                        ○ Unused
                      </button>
                    </div>
                  </div>

                  <div className="climate-info">
                    <small>Current: {climateData.currentTemp || climateData.current_temp}°C</small>
                    {climateData.lastUpdated && (
                      <small>Last updated: {new Date(climateData.lastUpdated || climateData.last_updated).toLocaleTimeString()}</small>
                    )}
                  </div>
                </div>
              ) : (
                <div className="climate-unavailable">
                  <p>Climate sensors not configured for this room.</p>
                </div>
              )}
            </div>
          </section>
        )}

        <hr className="section-divider" />

        <h2>New Booking</h2>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="booking-form">
          {/* Preferred Studio - FIRST */}
          <div className="form-row">
            <label>Preferred Studio *</label>
            <select
              name="roomId"
              value={formData.roomId}
              onChange={handleChange}
              required
            >
              <option value="">Select a studio...</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>
                  {room.displayName || room.name} - {room.organisation_name}
                </option>
              ))}
            </select>
          </div>

          {/* M004-F0065 & M004-F0066: Booking Type - enabled for Super Admin, Admin, Teacher; disabled for event_simple */}
          {user?.role !== 'event_simple' && (
            <div className="form-row">
              <label>Booking Type *</label>
              <select
                name="bookingType"
                value={formData.bookingType}
                onChange={handleChange}
                required
              >
                <option value="one-off">One-off</option>
                <option value="weekly">Weekly (Regular)</option>
              </select>
              <small className="form-hint">
                {formData.bookingType === 'weekly' 
                  ? 'Weekly bookings repeat on the same day/time and may have discounts applied' 
                  : 'One-off booking for a single date'}
              </small>
            </div>
          )}

          {/* Date Selection - Single date for both types */}
          <div className="form-row">
            <label>{formData.bookingType === 'weekly' ? 'Start Date *' : 'Date *'}</label>
            <input
              type="date"
              name="bookingDate"
              value={formData.bookingDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
            {formData.bookingType === 'weekly' && (
              <small className="form-hint">First session date - subsequent sessions will be on the same day of the week</small>
            )}
          </div>

          {/* M004-F0066: Number of Occurrences - Only for Weekly, disabled for event_simple */}
          {formData.bookingType === 'weekly' && user?.role !== 'event_simple' && (
            <div className="form-row">
              <label>Number of Weeks *</label>
              <select
                name="occurrences"
                value={formData.occurrences}
                onChange={handleChange}
                required
              >
                {[4, 6, 8, 10, 12, 16, 20, 24, 48, 52].map(num => (
                  <option key={num} value={num}>{num} weeks</option>
                ))}
              </select>
              <small className="form-hint">How many weeks should this booking repeat?</small>
            </div>
          )}

          {/* Time From */}
          <div className="form-row">
            <label>Time From *</label>
            <select
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              required
            >
              <option value="">Select start time...</option>
              {timeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Time To - M004-F0076: End times must be after start and in 1-hour increments */}
          <div className="form-row">
            <label>Time To * <small>(1 hour increments)</small></label>
            <select
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              required
              disabled={!formData.startTime}
            >
              <option value="">{formData.startTime ? 'Select duration...' : 'Select start time first'}</option>
              {endTimeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Sync/Availability Status */}
          {syncing && (
            <div className="availability-checking">
              <span className="spinner"></span> Syncing with Google Calendar...
            </div>
          )}

          {!syncing && checking && (
            <div className="availability-checking">
              <span className="spinner"></span> Checking availability...
            </div>
          )}

          {availability && !checking && !syncing && (
            <div className={`availability-result ${availability.available ? 'available' : 'unavailable'}`}>
              {availability.available ? (
                <>
                  <span className="availability-icon">✓</span>
                  <span className="availability-text">Studio is FREE for this time slot!</span>
                  {availability.simulated && <small className="simulated-note">(Pending confirmation)</small>}
                </>
              ) : (
                <>
                  <span className="availability-icon">✗</span>
                  <span className="availability-text">Not Available</span>
                  {availability.conflicts && (
                    <div className="conflicts-list">
                      <strong>Conflicts:</strong>
                      <ul>
                        {availability.conflicts.map((conflict, idx) => (
                          <li key={idx}>
                            {conflict.title}: {new Date(conflict.start_time).toLocaleString()} - {new Date(conflict.end_time).toLocaleString()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Class/Session Name */}
          <div className="form-row">
            <label>Class/Session Name *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Ballet Basics"
              required
            />
          </div>

          {/* Nature of Use */}
          <div className="form-row">
            <label>Nature of Use *</label>
            <select
              name="natureOfUse"
              value={formData.natureOfUse}
              onChange={handleChange}
              required
            >
              <option value="dance_class">Dance class</option>
              <option value="dance_workshop">Dance workshop</option>
              <option value="fitness_class">Fitness class</option>
              <option value="private_lesson">Private lesson/practice</option>
              <option value="rehearsal">Rehearsal</option>
              <option value="training_seminar">Training session / seminar</option>
              <option value="other">Other</option>
            </select>
          </div>

          {formData.natureOfUse === 'other' && (
            <div className="form-row">
              <label>Please Specify *</label>
              <input
                type="text"
                name="otherNatureDescription"
                value={formData.otherNatureDescription}
                onChange={handleChange}
                placeholder="Describe the nature of use"
                required
              />
            </div>
          )}

          {/* Description/Message */}
          <div className="form-row">
            <label>Additional Information</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Any additional details or special requirements..."
              rows="4"
            />
          </div>

          {/* Submit */}
          <div className="form-submit">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !availability?.available}
            >
              {submitting ? 'Submitting...' : 'Submit Booking Request'}
            </button>
          </div>

          {organisation?.booking_requires_approval && (
            <div className="approval-notice">
              <strong>Note:</strong> Your booking will require approval from an administrator before it is confirmed.
            </div>
          )}
        </form>
      </div>

      {/* Edit Booking Modal */}
      {showEditModal && (
        <div className="modal-overlay" {...editModalDismissHandlers}>
          <div className="modal" ref={editBookingDraft.containerRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Booking</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {editError && (
                  <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                    {editError}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="edit-title">Title *</label>
                  <input
                    id="edit-title"
                    type="text"
                    className="form-input"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-description">Description</label>
                  <textarea
                    id="edit-description"
                    className="form-input"
                    rows="3"
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-start">Start Time *</label>
                  <input
                    id="edit-start"
                    type="datetime-local"
                    className="form-input"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-end">End Time *</label>
                  <input
                    id="edit-end"
                    type="datetime-local"
                    className="form-input"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                    required
                  />
                </div>
                <p className="form-hint" style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  ⚠️ Note: Editing may require admin approval depending on your onboarding status.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BookRoomForm;
